<template>
  <div class="card">
    <header class="header-card">
      <p>
        {{ $t('materiel.collectivite.title') }}
      </p>
    </header>
    <div class="card-content columns is-multiline">
      <div class="column is-2 is-flex align-self-end mgb-10px">
        <qd-checkbox
          v-model="materiel.declarableEnMairie"
          @input="gestionFacesDeclarableEnMairie"
          class="has-text-default has-text-weight-bold">
          {{ $t('materiel.collectivite.declarableMairie') }}
        </qd-checkbox>
      </div>
      <div class="column is-5">
        <b-field :label="$t('materiel.collectivite.title')">
          <b-autocomplete :open-on-focus="false"
                          :data="collectiviteList"
                          :value="libelleCollectivite"
                          field="nomWithAdresseAndCompteComptable"
                          :placeholder="$i18n.t('materiel.collectivite.addCollectivite')"
                          @input="getCollectivite"
                          icon="magnify"
                          :loading="isFetchingCollectivite"
                          @select="selectCollectivite">
            <template slot="empty">
              <div v-if="!isFetchingCollectivite">
                {{$t('materiel.tlpe.actions.noResult')}}
              </div>
              <div v-else>
                {{$t('materiel.tlpe.actions.searchEnCours')}}
              </div>
            </template>
          </b-autocomplete>
        </b-field>
      </div>
      <div class="column is-4">
          <b-field :label="$t('materiel.descriptionTlpe.title')" class="obligatoire">
            <b-select v-model="materiel.descriptionTlpe" expanded>
              <option v-for="description in listeDescriptionTlpe"
                      :value="description.code" :key="description.code">
                {{ $t(description.libelle) }}
              </option>
            </b-select>
          </b-field>
      </div>
      <div class="column is-3">
        <b-field :label="$t('materiel.collectivite.etatMontageTlpe')" class="obligatoire">
          <b-select v-model="materiel.etatMontageTlpe" expanded>
            <option v-for="etat in listeEtatFilDeLeau"
                    :value="etat.code" :key="etat.code">
              {{ $t(etat.libelle) }}
            </option>
          </b-select>
        </b-field>
      </div>
      <div class="column is-3">
        <b-field :label="$t('materiel.collectivite.etatDemontagelpe')" class="obligatoire">
          <b-select v-model="materiel.etatDemontageTlpe" expanded>
            <option v-for="etat in listeEtatFilDeLeau"
                    :value="etat.code" :key="etat.code">
              {{ $t(etat.libelle) }}
            </option>
          </b-select>
        </b-field>
      </div>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import adressFullStringGetter from '@/services/general/adressFullStringGetter';
import debounce from 'lodash/debounce';
import { getAll, get } from '@/api/tiers';
import { TIERS_SOUS_TYPE_COLLECTIVITE, TIERS_TYPE_FOURNISSEUR } from '@/configuration/tiers/tiersConf';
import messageErreur from '@/services/general/messageErreur';
import messageInformation from '@/services/general/messageInformation';
import { LISTE_ETAT_FIL_DE_LEAU } from '@/configuration/gabed/materiel/materielConf';
import { LISTE_DESCRIPTION_TLPE } from '@/configuration/gabed/tlpe/tlpeConf';

const Societe = createNamespacedHelpers('societe');

export default {
  name: 'MaterielEditCollectivite',
  props: {
    materiel: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      libelleCollectivite: null,
      isFetchingCollectivite: false,
      collectiviteList: [],
      listeDescriptionTlpe: LISTE_DESCRIPTION_TLPE,
      listeEtatFilDeLeau: LISTE_ETAT_FIL_DE_LEAU,
    };
  },
  computed: {
    ...Societe.mapGetters({
      societeActiveId: 'societeActiveId',
    }),
  },
  methods: {
    getFormattageCollectivite(collectivite) {
      let libelle = collectivite.code + ' - ' + collectivite.nomCommercial;
      if (adressFullStringGetter(collectivite.adressePostale, false) !== '') {
        libelle += ' - ' + adressFullStringGetter(collectivite.adressePostale, false);
      }
      if (collectivite.compteComptable) {
        libelle += ' - ' + collectivite.compteComptable;
      }
      return libelle;
    },
    getCollectivite: debounce(async function collectiviteData(text) {
      if (text === this.libelleCollectivite) return;
      try {
        this.libelleCollectivite = text;
        this.isFetchingCollectivite = true;
        this.materiel.collectiviteId = null;
        const collectiviteList = (await getAll({
          query: {
            type: TIERS_TYPE_FOURNISSEUR,
            auto: text,
            sousType: [TIERS_SOUS_TYPE_COLLECTIVITE],
            societeId: this.societeActiveId,
            archive: false,
          },
        })).data.result.items;
        collectiviteList.map((collectivite) => {
          collectivite
            .nomWithAdresseAndCompteComptable = this.getFormattageCollectivite(collectivite);
          return collectivite;
        });
        this.collectiviteList = collectiviteList.filter((collectivite) => {
          return this.materiel.collectiviteId !== collectivite.id;
        });
        this.isFetchingCollectivite = false;
      } catch (e) {
        messageErreur(this.$i18n);
        this.isFetchingCollectivite = false;
        throw e;
      }
    }, 500),
    selectCollectivite(data) {
      if (data) {
        this.materiel.collectiviteId = cloneDeep(data.id);
        this.materiel.collectivite = cloneDeep(data);
        this.libelleCollectivite = cloneDeep(data.nomWithAdresseAndCompteComptable);
      }
    },
    gestionFacesDeclarableEnMairie() {
      if (!this.materiel.faces.length) return;
      if (!this.materiel.declarableEnMairie
          && this.materiel.faces.some(face => face.declarableEnMairie)) {
        this.materiel.faces.forEach(face => {
          face.declarableEnMairie = false;
        });
        messageInformation(this.$t('materiel.messageInformations.materielNonDeclarable'), 'is-warning');
      }
    },
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      if (this.materiel.collectiviteId) {
        this.materiel.collectivite = (await get(this.materiel.collectiviteId)).data.result;
        this.libelleCollectivite = this.materiel.collectiviteId
          ? this.getFormattageCollectivite(this.materiel.collectivite) : null;
      }
      loadingComponent.close();
    } catch (e) {
      loadingComponent.close();
      messageErreur(this.$i18n);
      throw e;
    }
  },
};
</script>
